import {
  Association,
  GoldenNotification,
  Group,
  PartnerOrganization,
  UserAvailability,
  UserNotification,
  UserPopulation,
  UserPopulationPreference,
  UserCategoryPreference,
  UserSkill,
  UserVehicle,
} from "@entities";
import { extender } from "@shared/backend/class-extender";
import HyperEntity from "@shared/backend/hyper-entity.decorator";
import { GENDER_LITERAL, GENDER_TO_HEB } from "@shared/helpers/constants";
import { JsonField } from "@shared/helpers/custom-fields";
import { isAdminsOrCurrentUser } from "@shared/helpers/include-in-api.function";
import { Roles } from "@shared/Roles";
import { Fields, Relations, remult, Validators } from "remult";
import { UserEntityConfig } from "./config";
import { UserFilters } from "./filters";
import { UserMethods } from "./methods";

@HyperEntity<User>("user", UserEntityConfig)
export class User extends extender(UserFilters, UserMethods) {
  @Fields.id()
  id = "";

  @Relations.toMany(() => Association, { field: "userId" })
  associations: Association[] = [];

  @Relations.toMany<User, UserAvailability>(() => UserAvailability, "userId")
  availability: UserAvailability[] = [];

  @JsonField({ dbName: "preferred_positions", caption: "עמדות מועדפות" })
  preferredPositions: string[] = [];

  @Fields.string({ validate: Validators.required, caption: "שם מלא" })
  name = "";

  @Fields.string({ dbName: "first_name", caption: "שם פרטי", includeInApi: isAdminsOrCurrentUser })
  firstName = "";

  @Fields.string({ dbName: "last_name", caption: "שם משפחה", includeInApi: isAdminsOrCurrentUser })
  lastName = "";

  @Fields.string({ caption: "כתובת", includeInApi: isAdminsOrCurrentUser })
  address = "";

  @Fields.boolean({ dbName: "is_mute", defaultValue: () => false })
  isMute = false;

  @Fields.string({
    includeInApi: isAdminsOrCurrentUser,
    dbName: "number_id",
    // Validate: (user: User, e) => {
    //   if (user.numberID !== "") return Validators.unique(user, e);
    // },
  })
  numberID = "";

  @JsonField<User, User["coordinates"]>({
    allowedKeys: ["lat", "lng"],
    includeInApi: isAdminsOrCurrentUser,
  })
  coordinates?: { lat: number; lng: number; };

  // PostGIS (geography(Point,4326)) - kept out of API; used for DB-level geo filtering/indexes
  @Fields.string({ dbName: "coordinates_geog", allowNull: true, includeInApi: false })
  coordinatesGeog: any = null;

  @Fields.literal(() => GENDER_LITERAL, {
    caption: "מגדר",
    allowNull: true,
    includeInApi: isAdminsOrCurrentUser,
    validate: (_, event) => {
      return event.value == null || GENDER_LITERAL.includes(event.value);
    },
    displayValue: (_, value) =>
      value ? GENDER_TO_HEB[value] : "",
  })
  gender: (typeof GENDER_LITERAL)[number] | null = null;

  @Fields.string({ includeInApi: isAdminsOrCurrentUser })
  email = "";

  @Fields.string({
    dbName: "partner_contact_role",
    allowNull: true,
    caption: "תפקיד בארגון שותף",
    includeInApi: isAdminsOrCurrentUser,
  })
  partnerContactRole?: string | null;

  @Fields.dateOnly({
    dbName: "date_of_birth",
    caption: "גיל",
    includeInApi: isAdminsOrCurrentUser,
    valueConverter: {
      displayValue: (val) => {
        const birthYear = new Date(val).getFullYear();
        const thisYear = new Date().getFullYear();
        if (!val || !birthYear) return "-";
        return String(thisYear - birthYear);
      },
    },
  })
  dateOfBirth: Date | null = new Date();

  // @Relations.toMany(() => GroupAssociation, {
  //   field: "userId",
  //   caption: "שיוך לקבוצה",
  // })
  // groups?: GroupAssociation[];

  @Relations.toMany(() => UserNotification, "toId")
  notifications: UserNotification[] = [];

  @Relations.toMany<User, GoldenNotification>(
    () => GoldenNotification,
    "userId",
  )
  goldenNotifications: GoldenNotification[] = [];

  @Relations.toMany(() => Group, "admin")
  managedGroups: Group[] = [];

  @Relations.toMany(() => UserPopulation, { field: "userId", caption: "אוכלוסיות" })
  populations: UserPopulation[] = [];

  @Relations.toMany(() => UserPopulationPreference, {
    field: "userId",
    caption: "העדפות אוכלוסיות",
  })
  populationPreferences: UserPopulationPreference[] = [];

  @Relations.toMany(() => UserCategoryPreference, {
    field: "userId",
    caption: "העדפות קטגוריות",
  })
  categoryPreferences: UserCategoryPreference[] = [];

  @Relations.toMany(() => UserSkill, { field: "userId", caption: "יכולות" })
  skills: UserSkill[] = [];

  @Relations.toMany(() => UserVehicle, "userId")
  vehicles: UserVehicle[] = [];

  @Fields.string({
    dbName: "phone_number",
    validate: [
      Validators.required,
      (user: User, e) => {
        if (user.name !== "משתמש נמחק") return Validators.unique(user, e);
      },
    ],
    caption: "מספר טלפון",
    // New users can set a phone. Existing phones stay locked, except a super admin editing a partner app manager.
    allowApiUpdate: (user) =>
      !user?.id ||
      (!!user.partnerOrganizationId && remult.isAllowed(Roles.SuperAdmins)),
    includeInApi: isAdminsOrCurrentUser,
    valueConverter: {
      displayValue(val) {
        if (!val) return "-";
        // Replace the country code with "0"
        let formattedVal = val?.replace("+972", "0");

        // Format the phone number as XXX-XXX-XXXXX
        formattedVal = formattedVal.replace(
          /^(\d{3})(\d{3})(\d+)$/,
          "$1-$2-$3",
        );

        return formattedVal;
      },
    },
  })
  phoneNumber = "";

  @Fields.string({ dbName: "profile_image_id" })
  profileImageId = "";

  @Fields.string({ dbName: "partner_organization_id", allowNull: true })
  partnerOrganizationId: string | null = null;

  @Relations.toOne<User, PartnerOrganization>(
    () => PartnerOrganization,
    "partnerOrganizationId",
  )
  partnerOrganization?: PartnerOrganization;

  @JsonField<User>({
    includeInApi: isAdminsOrCurrentUser,
    allowApiUpdate: false,
    validate: (user) => {
      if (user.roles.length === 1 && user.roles.includes(Roles.User))
        return true;
      else return false;
    },
  })
  roles: string[] = [Roles.User];

  @Fields.string({ dbName: "fcm_token", includeInApi: isAdminsOrCurrentUser })
  fcmToken: string | null = null;

  @Fields.boolean({
    dbName: "has_vehicle",
    includeInApi: isAdminsOrCurrentUser,
  })
  hasVehicle = false;

  @Fields.literal(
    () =>
      [
        "SEVERAL_TIMES_A_WEEK",
        "WEEKLY",
        "BIWEEKLY",
        "MONTHLY",
        "RARELY",
        "FLEXIBLE",
      ] as const,
    {
      dbName: "volunteer_frequency",
      allowNull: true,
      includeInApi: isAdminsOrCurrentUser,
    },
  )
  volunteerFrequency:
    | "SEVERAL_TIMES_A_WEEK"
    | "WEEKLY"
    | "BIWEEKLY"
    | "MONTHLY"
    | "RARELY"
    | "FLEXIBLE"
    | null = null;

  @Fields.literal(() => ["YES", "NO", "MAYBE"] as const, {
    dbName: "call_center_offer_preference",
    allowNull: true,
    includeInApi: isAdminsOrCurrentUser,
  })
  callCenterOfferPreference: "YES" | "NO" | "MAYBE" | null = null;

  @Fields.boolean({ dbName: "is_deleted", includeInApi: isAdminsOrCurrentUser })
  isDeleted = false;

  @Fields.boolean({
    dbName: "is_in_black_list",
    includeInApi: isAdminsOrCurrentUser,
    allowApiUpdate: false,
  })
  isInBlackList = false;

  @Fields.boolean({
    dbName: "is_verified",
    caption: "משתמש מאומת",
    includeInApi: isAdminsOrCurrentUser,
    allowApiUpdate: () => remult.isAllowed(Roles.AdminsAndManager),
  })
  isVerified = false;

  @Fields.date({ dbName: "last_entry", includeInApi: isAdminsOrCurrentUser })
  lastEntry?: Date;

  @Fields.createdAt({
    includeInApi: isAdminsOrCurrentUser,
    caption: "תאריך הרשמה",
    dbName: "created_at",
    valueConverter: {
      displayValue: (val) =>
        val ? new Date(val)?.toLocaleDateString("he") : "-",
    },
  })
  createdAt = new Date();

  @Fields.updatedAt({
    includeInApi: isAdminsOrCurrentUser,
    dbName: "updated_at",
  })
  updatedAt = new Date();
}
